// External Dependencies
import React, { useState } from 'react';
import PropTypes from 'prop-types';

// Internal Dependencies
import { useMutationCall } from '../../../api/react-client';

// UI Components
import { Button, Heading } from '../../../UiKit';
import { Info } from 'sushiweb/Icons';

/**
 * Modal component for resetting PIN and recovery mobile number
 * @component
 * @param {Object} props - Component props
 * @param {string|number} props.userId - Customer ID to reset PIN for
 * @param {Function} props.closePopup - Function to close the modal
 */
export const ResetPinModal = ({ userId: userId, closePopup }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API endpoint for resetting PIN - replace with your actual endpoint
  const resetPinEndpoint = `user/reset_restricted_mode`;

  // Mutation for resetting PIN
  const { mutate: resetPin, isLoading: isResetting } = useMutationCall(
    {
      onSuccess: (data) => {
        setIsSubmitting(false);
        if (data?.success) {
          closePopup();
        }
      },
      onError: () => {
        setIsSubmitting(false);
      },
    },
    resetPinEndpoint,
    [],
  );

  const handleConfirm = () => {
    setIsSubmitting(true);

    const dataToSubmit = {
      user_id: userId,
    };

    resetPin(dataToSubmit);
  };

  const handleCancel = () => {
    closePopup();
  };

  return (
    <div className='tw-px-4'>
      {/* Warning Banner */}
      <div className='tw-px-4 tw-p-4 tw-bg-yellow-100 tw-border tw-border-yellow-300 tw-rounded-lg tw-flex tw-flex-col tw-gap-2 tw-mb-6'>
        <div className='tw-text-yellow-800 tw-font-medium tw-flex tw-gap-2 tw-items-start'>
          <Info size={16} className='tw-mt-0.5 tw-flex-shrink-0' />
          <div className='tw-text-sm tw-leading-relaxed'>
            This will reset the PIN and recovery mobile number for managing
            sensitive item hiding across all devices. Hiding sensitive items
            will be disabled until a new PIN and recovery number are set
          </div>
        </div>
      </div>

      {/* Modal Content */}
      <div className='tw-mb-6'>
        <Heading title={`Customer ID: ${userId}`} />
        <p className='tw-text-gray-600 tw-mt-2 tw-text-sm'>
          Are you sure you want to proceed with resetting the PIN and recovery
          mobile number for hiding sensitive items?
        </p>
      </div>

      {/* Action Buttons */}
      <div className='tw-flex tw-justify-end tw-gap-3 tw-mt-8'>
        <Button
          title='Cancel'
          isDisabled={isSubmitting || isResetting}
          onClick={handleCancel}
          outline={true}
          size='small'
          variant='secondary'
        />
        <Button
          title='Reset'
          isDisabled={isSubmitting || isResetting}
          loading={isSubmitting || isResetting}
          onClick={handleConfirm}
          outline={false}
          size='small'
          variant='primary'
        />
      </div>
    </div>
  );
};

// PropTypes
ResetPinModal.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  closePopup: PropTypes.func.isRequired,
};

ResetPinModal.defaultProps = {
  closePopup: () => {},
};

export default ResetPinModal;
